import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BASE_URL, PagedResponse } from '@edl-development/edl-common';
import { PagedModel } from '../model/common/paged.model';
import { CplArchiveCycle } from '../model/cpl-archive-cycle.model';
import { CplCycle } from '../model/cpl-cycle.model';
import { CplItemDuplicateData } from '../model/cpl-item-duplicate-data.model';
import { CplItemSummary } from '../model/cpl-item-summary.model';
import { CplItem } from '../model/cpl-item.model';
import { CplItemPermissions } from '../model/enum/cpl-item-permissions.enum';
import { UserAssignmentRequest } from '../model/user-assignment-request.model';
import { UserSummary } from '../model/user-summary.model';
import { ComplyBaseApi } from './comply/comply-base.api';
import { CplUserCompliance } from '../model/user-compliance/cpl-user-compliance.model';
import { CplUserStatusSummary } from '../model/user-compliance/cpl-user-status-summary.model';
import { CplItemSubmissionStatusFilter } from '../supervision-management/enums/cpl-item-submission-status-filter.enum';

@Injectable()
export class ComplianceItemApi extends ComplyBaseApi {
    constructor(
        @Inject(BASE_URL) private baseUri: string,
        private http: HttpClient
    ) {
        super();
    }

    getComplianceItem(cplId: string, cycleId: string) {
        return this.http.get<CplItem>(this.baseUri + '/cplitem/' + cplId + '?cycleID=' + cycleId);
    }

    getMyComplianceItems(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy: string, status: number, filterByTag: string, filterByItemPacket: string[], submissionStatus?: CplItemSubmissionStatusFilter) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(status))
            .append('tag', this.convertParam(filterByTag))
            .append('itemPackets', this.convertArrayParam(filterByItemPacket))
            .append('submissionStatus', String(submissionStatus ?? CplItemSubmissionStatusFilter.ALL));

        return this.http.get<PagedResponse<CplItemSummary>>(this.baseUri + '/cplitems/myitems', {
            params
        })
    }

    getComplianceItemsAssignedToUser(userId: string) {
        return this.http.get<CplItemSummary[]>(this.baseUri + '/cplitems/' + userId);
    }

    getComplianceItems(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy: string, status: number, tag: number, itemPackets: string[], submissionStatus?: CplItemSubmissionStatusFilter) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(status))
            .append('tag', String(tag))
            .append('itemPackets', this.convertArrayParam(itemPackets))
            .append('submissionStatus', String(submissionStatus ?? CplItemSubmissionStatusFilter.ALL));

        return this.http.get<PagedResponse<CplItemSummary>>(this.baseUri + '/cplitems', {
            params
        });
    }

    getNewItemPermissions = () => {
        return this.http.get<CplItemPermissions[]>(`${this.baseUri}/cplitem/newItemPermissions`);
    }

    createComplianceItem(complianceItem: CplItem) {
        return this.http.post(this.baseUri + '/cplitem', complianceItem);
    }

    updateComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem', complianceItem);
    }

    deleteComplianceItem(complianceItem: CplItem) {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: complianceItem
        };

        return this.http.request('delete', this.baseUri + '/cplitem', {
            body: complianceItem
        });
    }

    restoreComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem/restore', complianceItem, { responseType: 'text' });
    }

    removeComplianceItem(complianceItem: CplItem) {
        return this.http.request('delete', this.baseUri + '/cplitem/remove', {
            responseType: 'text',
            body: complianceItem
        });
    }

    archiveComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem/archive', complianceItem);
    }

    getArchiveWarnings(cplId: string) {
        return this.http.get(this.baseUri + '/cplitem/archiveWarnings/' + cplId);
    }

    activateComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem/activate', complianceItem, { responseType: 'text' });
    }

    convertComplianceItemToTemplate(complianceItemDuplicateData: CplItemDuplicateData) {
        return this.http.put(this.baseUri + '/cplitem/template', complianceItemDuplicateData);
    }

    duplicateComplianceItem(complianceItemDuplicateData: CplItemDuplicateData) {
        return this.http.put(this.baseUri + '/cplitem/duplicate', complianceItemDuplicateData);
    }

    requestAccess(cplItemId: string) {
        return this.http.post(`${this.baseUri}/cplitem/requestAccess/${cplItemId}`, {});
    }

    getItemUserAssignments({
        cplId,
        cycleId,
        offset,
        limit,
        orderBy,
        ascending,
        searchBy,
        statusId,
        a48statusId,
        approverUserId,
        groupIds
    }: {
        cplId: string,
        cycleId: string,
        offset: number,
        limit: number,
        orderBy?: string,
        ascending?: boolean,
        searchBy?: string,
        statusId?: number,
        a48statusId?: number,
        approverUserId?: string,
        groupIds?: string[]
    }) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', String(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(statusId))
            .append('a48status', a48statusId ? String(a48statusId) : "")
            .append('approverUserId', approverUserId ? String(approverUserId) : '')
            .append('groupIds', this.convertArrayParam(groupIds));

        return this.http.get(this.baseUri + '/UserAssignments/' + cplId + '/' + cycleId, {
            params
        });
    }

    getUserUserAssignments({
        cplId,
        offset,
        limit,
        orderBy,
        ascending,
        searchBy,
        statusId,
        a48statusId,
        approverUserId,
        groupIds
    }: {
        cplId: string,
        offset: number,
        limit: number,
        orderBy?: string,
        ascending?: boolean,
        searchBy?: string,
        statusId?: number,
        a48statusId?: number,
        approverUserId?: string,
        groupIds?: string[]
    }) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', String(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(statusId))
            .append('a48status', a48statusId ? String(a48statusId) : "")
            .append('approverUserId', approverUserId ? String(approverUserId) : '')
            .append('groupIds', this.convertArrayParam(groupIds));

        return this.http.get(this.baseUri + '/UserAssignments/' + cplId, {
            params
        });
    }

    removeUserAssignments(request: UserAssignmentRequest) {
        return this.http.request('delete', this.baseUri + '/UserAssignment', {
            responseType: 'text',
            body: request
        });
    }

    createUserAssignments(request: UserAssignmentRequest) {
        return this.http.post(this.baseUri + '/UserAssignment', request, { responseType: 'text' });
    }

    createBulkUserAssignments(fileList: FileList, ItemID: string) {
        let file: File = fileList[0];
        let formData: FormData = new FormData();
        formData.append('file', file, file.name);
        formData.append('ItemID', ItemID);

        return this.http.post(this.baseUri + '/UserAssignment/bulk', formData);
    }

    createBulkSingleUserToMultipleAssignments(requests: UserAssignmentRequest[]) {
        return this.http.post(this.baseUri + '/UserAssignment/bulk/SingleUserToMultiple', requests);
    }

    updateUserAssignments(userCycles: CplCycle[]) {
        return this.http.put(this.baseUri + '/UserAssignments', userCycles, { responseType: 'text' });
    }

    getUserSummary(userId: string, paging: PagedModel, itemPackets: string[] = []) {
        const params = this.generatePagingParams(paging)
            .append('itemPackets', this.convertArrayParam(itemPackets ?? []));

        return this.http.get<PagedResponse<UserSummary>>(this.baseUri + '/UserSummary/' + userId, {
            params
        });
    }
    getUserArchiveSummary(userId: string) {

        return this.http.get<CplArchiveCycle[]>(this.baseUri + '/UserSummary/ArchiveSuper/' + userId);
    }

    getUserCycleHistoryByCycle(cycleId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/UserAssignment/Cycle/History/' + cycleId, {
            params
        });
    }

    getExpiredCycles(cplId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/UserAssignment/expired/' + cplId, {
            params
        });
    }
    getUserCycleHistory(cplId: string, userId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/UserAssignmentHistory/' + cplId + '/' + userId, {
            params
        });
    }

    getUserCompliance(paging: PagedModel, groups: string[] = []) {
        const params = this.generatePagingParams(paging)
            .append('groups', this.convertArrayParam(groups ?? []));

        return this.http.get<PagedResponse<CplUserCompliance>>(this.baseUri + '/usercompliance', {
            params
        });
    }

    getUserStatusSummary(userId: string) {
        return this.http.get<CplUserStatusSummary>(`${this.baseUri}/userStatusSummary/${userId}`);
    }


    getTags(searchBy: string) {
        return this.http.get(this.baseUri + '/cplitem/tag/' + searchBy);
    }



    getPrintableRoster(complianceItemId: number) {
        return this.http.get(this.baseUri + '/UserAssignments/roster/' + complianceItemId.toString());
    }
}